import {GridEvent} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridEvent';
import {
    CellArea,
    ColumnIndex,
    ColumnKey,
    GridEventListener,
    IGridAppearance,
    IGridCell,
    IGridModelView,
    IMutableGridModel,
    OnFillCell,
    OnGetCellArea,
    OnGetToColumnIndex,
    OnGetToRowIndex,
    RowIndex,
    VisibleColumnKeys
} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridModel';
import {TextHeaderCell} from '@mintdata-wsp/wsp/ui/components/grid/cells/columnHeader/TextHeaderCell';
import {TextCell} from '@mintdata-wsp/wsp/ui/components/grid/cells/TextCell';
import {
    GridCleared,
    GridColumnsAdded,
    GridColumnsFilterChanged,
    GridColumnWidthChanged,
    GridInitialRowsAdded,
    GridIsTransposedChanged,
    GridRowsAdded,
    GridRowsAndColumnsAdded, GridRowsCleared,
    GridRowSelectionChanged
} from '@mintdata-wsp/wsp/ui/components/grid/GridEvent';
import {GridModelPrinter} from '@mintdata-wsp/wsp/ui/components/grid/GridModelPrinter';
import {GridModelView} from '@mintdata-wsp/wsp/ui/components/grid/GridModelView';
import {TransposedGridModelView} from '@mintdata-wsp/wsp/ui/components/grid/TransposedGridModelView';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {IDisposable} from '@mintdata/mint-kit/api/resources/IDisposable';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import {action, observable} from 'mobx';

export class GridState
{
    @observable
    public isTransposed:boolean = false;

    @observable
    public columnFilter:VisibleColumnKeys = null;

    public isHaveSeenAddedRows:boolean = false;
    public numRows:number = 0;
    public numColumns:number = 0;

    public cells:IGridCell[][] = [];
    public columnStates:Map<ColumnIndex, ColumnState> = new Map();
    public rowStates:Map<RowIndex, RowState> = new Map();

    // public cellStates:Map<RowIndex, Map<ColumnIndex, CellState>> = new Map();

    public listeners:GridEventListener[] = [];
}

export class ColumnState
{
    @observable
    public width:number = 0;

    constructor(public readonly key:ColumnKey)
    {
    }
}

export class RowState
{
    @observable
    public height:number = 25;

    @observable
    public isSelected:boolean = false;
}

export class CellState
{
    @observable
    public isHighlighted:boolean = false;
}

export class GridModel implements IMutableGridModel
{
    private readonly _id:GUID = IdUtil.newGUID();

    private static _ROW_HEADER_COLUMN_KEY:ColumnKey = '__rowHeader';

    private readonly _appearance:IGridAppearance;
    private _state:GridState = new GridState();

    private constructor(appearance:IGridAppearance)
    {
        this._appearance = appearance;
        this._initCellState();
    }

    public static withAppearance(appearance:IGridAppearance):IMutableGridModel
    {
        return new GridModel(appearance);
    }

    @bind
    getId():GUID
    {
        return this._id;
    }

    @bind
    toString()
    {
        return `[GridModel id#${this.getId()}]`;
    }

    getAppearance():IGridAppearance
    {
        return this._appearance;
    }

    @bind
    _initCellState():void
    {
        const state = this._state;

        state.columnStates.set(0, new ColumnState(GridModel._ROW_HEADER_COLUMN_KEY));
        state.numColumns = 1;

        state.rowStates.set(0, new RowState());
        state.numRows = 1;

        state.cells = [[TextHeaderCell.fixed('', 20)]];
    }

    @bind
    setCornerCell(cornerCell:IGridCell):void
    {
        const state = this._state;
        state.cells[0][0] = cornerCell;
    }

    @bind
    addListener(listener:GridEventListener):IDisposable
    {
        const state = this._state;
        state.listeners.push(listener);

        return {
            dispose: () =>
            {
                const idx = state.listeners.findIndex((l:GridEventListener) => (l === listener));
                if( idx !== -1 )
                    state.listeners.splice(idx, 1);
            },
        };
    }

    @bind
    private _fireEvent(e:GridEvent):void
    {
        const state = this._state;

        for( let listener of state.listeners )
            listener(e);
    }

    @bind
    isTransposed():boolean
    {
        return this._state.isTransposed;
    }

    @action.bound
    setIsTransposed(isTransposed:boolean)
    {
        const state = this._state;
        state.isTransposed = isTransposed;

        this._fireEvent(new GridIsTransposedChanged(isTransposed));
    }

    @bind
    getColumnFilter():VisibleColumnKeys | null
    {
        const state = this._state;
        return state.columnFilter;
    }

    @bind
    setColumnFilter(visibleColumns:VisibleColumnKeys):void
    {
        for( let columnKey of visibleColumns )
        {
            if( this.getIndexOfColumn(columnKey, false) === -1 )
            {
                console.error(`Error: cannot setColumnFilter(): invalid column key (not found in grid model): ${columnKey}`);
                return;
            }
        }

        const oldFilter = this._state.columnFilter;
        this._state.columnFilter = visibleColumns;
        this._fireEvent(new GridColumnsFilterChanged(oldFilter, visibleColumns));
    }

    @bind
    getIndexOfColumn(key:ColumnKey, isRowHeaderColumnIncluded:boolean):ColumnIndex | -1
    {
        const idx = this._findColumnIndex(key);

        if( idx === -1 )
            return idx;
        else
        {
            if( isRowHeaderColumnIncluded )
                return idx;
            else
                return idx === 0 ? -1          // don't return rowHeader column (even if found)
                    :
                    idx - 1;    // return with offset relative to grid body
        }
    }

    private _findColumnIndex(columnKey:ColumnKey):number | -1
    {
        let idx = -1;

        this._state.columnStates.forEach((value:ColumnState, columnIndex:ColumnIndex) =>
        {
            if( value.key === columnKey )
                idx = columnIndex;
        });

        return idx;
    }

    @bind
    getColumnKeyAtIndex(columnIndex:ColumnIndex, isRowHeaderColumnIncluded:boolean):ColumnKey
    {
        if( !isRowHeaderColumnIncluded )
            columnIndex += 1;

        if( !this._isColumnIndexValid(columnIndex) )
            return;

        return this._state.columnStates.get(columnIndex).key;
    }

    @action.bound
    setColumnWidth(columnIndex:ColumnIndex, newWidth:number):number
    {
        if( !this._isColumnIndexValid(columnIndex) )
        {
            console.error(`Error: setColumnWidth(): invalid columnIndex: ${columnIndex} newWidth: ${newWidth} numColumns: ${this._state.numColumns}`);
            return;
        }

        this._state.columnStates.get(columnIndex).width = newWidth;
        this._fireEvent(new GridColumnWidthChanged(columnIndex));
    }

    @bind
    getColumnWidth(columnIndex:ColumnIndex):number
    {
        if( !this._isColumnIndexValid(columnIndex) )
        {
            console.error(`Error: getColumnWidth(): invalid columnIndex: ${columnIndex} numColumns: ${this._state.numColumns}`);
            return;
        }

        return this._state.columnStates.get(columnIndex).width;
    }

    @bind
    getRowHeight(rowIndex:RowIndex):number
    {
        if( !this._isRowIndexValid(rowIndex) )
            return;

        const rowState = this._getOrCreateRowState(rowIndex);
        return rowState.height;
    }

    @action.bound
    setRowHeight(rowIndex:RowIndex, newHeight:number):number
    {
        if( !this._isRowIndexValid(rowIndex) )
            return;

        const rowState = this._getOrCreateRowState(rowIndex);
        rowState.height = newHeight;
    }

    @action.bound
    setRowIsSelected(rowIndex:RowIndex, isSelected:boolean):void
    {
        if( !this._isRowIndexValid(rowIndex) )
            return;

        if( isSelected )
            this._deselectAllRows(rowIndex);

        const rowState = this._getOrCreateRowState(rowIndex);
        rowState.isSelected = isSelected;

        this._fireEvent(new GridRowSelectionChanged(rowIndex, isSelected));
    }

    @bind
    private _deselectAllRows(excludingRowIndex:RowIndex)
    {
        for( let rowIndex = 0; rowIndex < this._state.numRows; rowIndex++ )
        {
            if( rowIndex != excludingRowIndex )
            {
                const rowState = this._getOrCreateRowState(rowIndex);

                if( rowState.isSelected )
                    rowState.isSelected = false;
            }
        }
    }

    @bind
    isRowSelected(rowIndex:RowIndex):boolean
    {
        if( !this._isRowIndexValid(rowIndex) )
            return;

        const rowState = this._getOrCreateRowState(rowIndex);
        return rowState.isSelected;
    }

    @bind
    clear():void
    {
        const currentListeners = this._state.listeners;
        const isTransposed = this._state.isTransposed;

        this._state = new GridState();
        this._state.listeners = currentListeners;
        this._state.isTransposed = isTransposed;
        this._state.isHaveSeenAddedRows = false;

        this._initCellState();
        this._fireEvent(new GridCleared());
    }

    clearDataRows():void
    {
        const state = this._state;
        state.rowStates = new Map();

        if( state.numRows > 0 )
        {
            const columnHeadersRow = state.cells[0];
            state.cells = [columnHeadersRow];
        }
        else
            state.cells = [];

        state.numRows = 1;

        this._fireEvent(new GridRowsCleared());
    }

    @bind
    private _getOrCreateRowState(rowIndex:RowIndex)
    {
        let rowState = this._state.rowStates.get(rowIndex);

        if( !rowState )
        {
            rowState = new RowState();
            this._state.rowStates.set(rowIndex, rowState);
        }

        return rowState;
    }

    @bind
    _isColumnIndexValid(columnIndex:ColumnIndex):boolean
    {
        const numColumns = this._state.numColumns;

        if( columnIndex < 0 || columnIndex > numColumns - 1 )
        {
            console.error(`Error: invalid column index: ${columnIndex} (# columns: ${numColumns})`);
            return false;
        }

        return true;
    }

    @bind
    _isRowIndexValid(rowIndex:RowIndex):boolean
    {
        const numRows = this._state.numRows;

        if( rowIndex < 0 || rowIndex > numRows - 1 )
        {
            console.error(`Error: invalid row index: ${rowIndex} (# rows: ${numRows})`);
            return false;
        }

        return true;
    }

    @bind
    getColumnKeys():ColumnKey[]
    {
        const {columnStates} = this._state;

        return Array.from(columnStates.values())
            .map(({key}) => key)
            .filter((key) => key !== GridModel._ROW_HEADER_COLUMN_KEY);
    }

    @bind
    getColumnHeaders():IGridModelView
    {
        const state = this._state;

        const fromRow = 0;
        const toRow = () => (0);
        const fromCol = 0;
        const toCol = () => (state.numColumns - 1);

        return this._getView(fromRow, toRow, fromCol, toCol,
            (rowIndex:RowIndex, columnIndex:ColumnIndex) => (columnIndex === 0 ? CellArea.TopLeftCorner : CellArea.ColumnHeader));
    }

    @bind
    getRowHeaders():IGridModelView
    {
        const state = this._state;

        const fromRow = 0;
        const toRow = () => (state.numRows - 1);
        const fromCol = 0;
        const toCol = () => (0);

        return this._getView(fromRow, toRow, fromCol, toCol,
            (rowIndex:RowIndex, columnIndex:ColumnIndex) => (rowIndex === 0 ? CellArea.TopLeftCorner : CellArea.RowHeader));
    }

    getContentView():IGridModelView
    {
        const appearance = this.getAppearance();
        const rowHeaderVisible = appearance.isRowHeaderVisible;
        const columnHeaderVisible = appearance.isColumnHeaderVisible;

        if( rowHeaderVisible && columnHeaderVisible )
            return this.getBodyWithBothHeaders();
        else if( rowHeaderVisible )
            return this.getBodyWithRowHeaders();
        else if( columnHeaderVisible )
            return this.getBodyWithColumnHeaders();
        else
            return this.getBody();
    }

    @bind
    getBody():IGridModelView
    {
        const state = this._state;

        const fromRow = 1;
        const toRow = () => (state.numRows - 1);
        const fromCol = 1;
        const toCol = () => (state.numColumns - 1);

        return this._getView(fromRow, toRow, fromCol, toCol, () => (CellArea.Body));
    }

    @bind
    getBodyWithRowHeaders():IGridModelView
    {
        const state = this._state;

        const fromRow = 1;
        const toRow = () => (state.numRows - 1);
        const fromCol = 0;
        const toCol = () => (state.numColumns - 1);

        return this._getView(fromRow, toRow, fromCol, toCol,
            (rowIndex:RowIndex, columnIndex:ColumnIndex) =>
            {
                if( rowIndex === 0 && columnIndex === 0 )
                    return CellArea.TopLeftCorner;
                else
                {
                    if( columnIndex === 0 )
                        return CellArea.RowHeader;
                    else
                        return CellArea.Body;
                }
            });
    }

    @bind
    getBodyWithColumnHeaders():IGridModelView
    {
        const state = this._state;

        const fromRow = 0;
        const toRow = () => (state.numRows - 1);
        const fromCol = 1;
        const toCol = () => (state.numColumns - 1);

        return this._getView(fromRow, toRow, fromCol, toCol,
            (rowIndex:RowIndex, columnIndex:ColumnIndex) =>
            {
                if( rowIndex === 0 )
                    return CellArea.ColumnHeader;
                else
                    return CellArea.Body;
            });
    }

    @bind
    getBodyWithBothHeaders():IGridModelView
    {
        const state = this._state;

        const fromRow = 0;
        const toRow = () => (state.numRows - 1);
        const fromCol = 0;
        const toCol = () => (state.numColumns - 1);

        return this._getView(fromRow, toRow, fromCol, toCol,
            (rowIndex:RowIndex, columnIndex:ColumnIndex) =>
            {
                if( rowIndex === 0 && columnIndex === 0 )
                    return CellArea.TopLeftCorner;
                else
                {
                    if( columnIndex === 0 )
                        return CellArea.RowHeader;
                    else if( rowIndex === 0 )
                        return CellArea.ColumnHeader;
                    else
                        return CellArea.Body;
                }
            });
    }

    @bind
    private _getView(fromRow:number,
                     toRow:OnGetToRowIndex,
                     fromCol:number,
                     toCol:OnGetToColumnIndex,
                     getCellArea:OnGetCellArea):IGridModelView
    {
        const state = this._state;
        const isTransposed = this.isTransposed();

        if( isTransposed )
            return new TransposedGridModelView(getCellArea, state, this, fromRow, toRow, fromCol, toCol);
        else
            return new GridModelView(getCellArea, state, this, fromRow, toRow, fromCol, toCol);
    }

    @bind
    getRawCell(rowIndex:RowIndex, columnIndex:number):IGridCell | null
    {
        const cells = this._state.cells;

        const numRows = cells.length;
        if( rowIndex < 0 || rowIndex > numRows - 1 )
        {
            console.error(`Error: invalid rowIndex: ${rowIndex} (# rows: ${numRows})`);
            return null;
        }

        const row = cells[rowIndex];
        if( !row )
        {
            console.error(`Error: initialized row doesn't exist at rowIndex: ${rowIndex}`);
            return null;
        }

        const numColumns = row.length;
        if( columnIndex < 0 || columnIndex > numColumns - 1 )
        {
            console.error(`Error: invalid columnIndex: ${columnIndex} (# columns: ${numColumns})`);
            return null;
        }

        return row[columnIndex];
    }

    @bind
    appendColumns(columnKeys:ColumnKey[], createColumnHeader:OnFillCell, cellFiller:OnFillCell):void
    {
        this._appendColumns(columnKeys, createColumnHeader, cellFiller, true);
    }

    @bind
    _appendColumns(columnKeys:ColumnKey[], createColumnHeader:OnFillCell, cellFiller:OnFillCell, shouldFireEvent):void
    {
        const fromColumnIndex = this._state.numColumns;

        for( let columnKey of columnKeys )
            this._appendColumn(columnKey, createColumnHeader, cellFiller);

        // Fire: event if needed
        if( shouldFireEvent )
            this._fireEvent(new GridColumnsAdded(fromColumnIndex, fromColumnIndex + columnKeys.length - 1));
    }

    @bind
    private _appendColumn(columnKey:ColumnKey,
                          createColumnHeader:OnFillCell,
                          cellFiller:OnFillCell)
    {
        if( this.getIndexOfColumn(columnKey, true) !== -1 )
        {
            console.error(`Error: column key already exists: ${columnKey}, cannot appendColumn()`);
            return;
        }

        // Add: column state
        const state = this._state;
        state.columnStates.set(state.numColumns, new ColumnState(columnKey));

        // Add: columnHeader cell
        const columnHeaderCell = createColumnHeader(0, state.numColumns - 1, columnKey);
        state.cells[0].push(columnHeaderCell);

        // Backfill: previous rows
        this._backfillExistingRowsWithNewColumn(cellFiller, columnKey);

        // Increment: num columns
        state.numColumns++;
    }

    private _backfillExistingRowsWithNewColumn(cellFiller:OnFillCell, columnKey:ColumnKey)
    {
        const state = this._state;

        for( let rowIndex = 1; rowIndex < state.numRows; rowIndex++ )
        {
            const columnIndex = state.numColumns;
            const fillerCell = cellFiller(rowIndex, columnIndex, columnKey);

            const row = this._getOrCreateRow(rowIndex);
            row[columnIndex] = fillerCell;
        }
    }

    @bind
    private _getOrCreateRow(rowIndex:RowIndex):IGridCell[]
    {
        const state = this._state;
        let row = state.cells[rowIndex];

        if( !row )
        {
            row = [] as IGridCell[];
            state.cells[rowIndex] = row;
        }

        return row;
    }

    @bind
    appendDataRow(row:IGridCell[], createRowHeader:OnFillCell):void
    {
        this._appendDataRow(row, createRowHeader, true);
    }

    //(rowIndex:RowIndex, columnIndex:ColumnIndex, key:ColumnKey)=>(IGridCell);
    @bind
    private _createDefaultRowHeader(rowIndex:RowIndex):IGridCell
    {
        return new TextCell('' + rowIndex);
    }

    @bind
    private _appendDataRow(row:IGridCell[], createRowHeader:OnFillCell, shouldFireEvent:boolean)
    {
        if( !row )
        {
            console.error('Error: invalid null/undefined row');
            return;
        }

        const state = this._state;
        if( row.length !== state.numColumns - 1 )
        {
            console.error(`Error: invalid row size, row.length: ${row.length} numColumns: ${state.numColumns}`);
            return;
        }

        const rowIndex = state.numRows;
        const rowHeaderCell = createRowHeader ?
            createRowHeader(rowIndex, 0, GridModel._ROW_HEADER_COLUMN_KEY) :
            this._createDefaultRowHeader(rowIndex);

        const fullRow = [rowHeaderCell].concat(row);

        state.cells[rowIndex] = fullRow;
        state.rowStates.set(rowIndex, new RowState());

        state.numRows++;

        if( shouldFireEvent )
            this._fireRowsAddedEvent(state.numRows - 1, state.numRows);
    }

    @bind
    private _fireRowsAddedEvent(fromRowIndex:number, toRowIndex:number):void
    {
        if( !this._state.isHaveSeenAddedRows )
        {
            this._state.isHaveSeenAddedRows = true;
            this._fireEvent(new GridInitialRowsAdded(fromRowIndex, toRowIndex));
        }

        this._fireEvent(new GridRowsAdded(fromRowIndex, toRowIndex));
    }

    @bind
    appendDataRowsAndColumns(rows:IGridCell[][],
                             createRowHeader:OnFillCell,
                             newColumnKeys:ColumnKey[],
                             createColumnHeader:OnFillCell,
                             cellFiller:OnFillCell):void
    {
        if( !rows || rows.length < 1 )
        {
            console.error(`Error: empty/undefined rows passed to appendDataRowsAndColumns()`);
            return;
        }

        const fromColumnIndex = this._state.numColumns;
        this._appendColumns(newColumnKeys, createColumnHeader, cellFiller, false);
        const toColumnIndex = fromColumnIndex + newColumnKeys.length - 1;

        const fromRowIndex = this._state.numRows;

        for( let row of rows )
            this._appendDataRow(row, createRowHeader, false);

        const toRowIndex = this._state.numRows - 1;

        if( !this._state.isHaveSeenAddedRows )
        {
            this._fireRowsAddedEvent(fromRowIndex, toRowIndex);
            this._fireEvent(new GridColumnsAdded(fromColumnIndex, toColumnIndex));
        }
        else
        {
            if( newColumnKeys && newColumnKeys.length > 0 )
                this._fireEvent(new GridRowsAndColumnsAdded(fromRowIndex, toRowIndex, fromColumnIndex, toColumnIndex));
            else
                this._fireEvent(new GridRowsAdded(fromRowIndex, toRowIndex));
        }
    }

    @bind
    print():void
    {
        GridModelPrinter.print(this);
    }
}
